import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { useI18n } from "@/i18n";

/**
 * Listens for `ks:paywall` events (emitted by the axios interceptor when the
 * backend returns 403 with `detail.code === "plan_required"`) and renders
 * the Unlock-KeepSoul modal. Free users can browse the UI but the moment
 * they try to create/save in Vault, Messages, Heirs, Digital Assets,
 * Trusted Contacts, Stories or Proof of Life this modal fires.
 */
export default function PaywallProvider({ children }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onPaywall = () => setOpen(true);
    window.addEventListener("ks:paywall", onPaywall);
    return () => window.removeEventListener("ks:paywall", onPaywall);
  }, []);

  const close = () => setOpen(false);
  const goToPlans = () => {
    setOpen(false);
    navigate("/plans");
  };

  return (
    <>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ background: "rgba(13,12,11,0.85)", backdropFilter: "blur(8px)" }}
          data-testid="paywall-modal"
          role="dialog"
          aria-modal="true"
          onClick={close}
        >
          <div
            className="glass-card rounded-lg max-w-md w-full p-8 sm:p-10 relative border border-gold-subtle"
            style={{ background: "#0d0c0b" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute top-3 right-3 text-faded hover:text-gold-light p-1"
              aria-label="X"
              data-testid="paywall-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex justify-center mb-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: "rgba(201,169,110,0.12)", border: "1px solid rgba(201,169,110,0.35)" }}
              >
                <Lock className="w-7 h-7 text-gold" strokeWidth={1.4} />
              </div>
            </div>

            <h2
              className="font-display text-3xl sm:text-4xl text-gold-light text-center mb-3"
              data-testid="paywall-title"
            >
              {t("paywall_title")}
            </h2>
            <p className="text-muted text-sm sm:text-base text-center mb-8 leading-relaxed">
              {t("paywall_body")}
            </p>

            <button
              onClick={goToPlans}
              className="btn-gold w-full justify-center text-base py-3"
              data-testid="paywall-cta"
            >
              {t("paywall_cta")}
            </button>

            <p className="text-faded text-[11px] text-center mt-4">
              {t("paywall_hint")}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
